import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { fmtDate } from "@/lib/dates";
import { Award, Upload, FileText, Trash2, Loader2, ExternalLink } from "lucide-react";

export function ChampionCertificate() {
  const [cert, setCert] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { api.get("/care-home/champion").then((r) => setCert(r.data.champion_certificate || null)).catch(() => {}); }, []);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/care-home/champion-certificate", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCert(data.champion_certificate); toast.success("Oral Champion certificate uploaded");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };
  const remove = async () => {
    if (!window.confirm("Remove the uploaded certificate?")) return;
    try { await api.delete("/care-home/champion-certificate"); setCert(null); toast.success("Certificate removed"); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="champion-certificate" className="mb-6 rounded-2xl border border-cream-deep bg-cream-card p-5 flex flex-wrap items-center justify-between gap-4">
      <div className="flex items-start gap-3 min-w-0">
        <div className="h-10 w-10 rounded-full bg-brand-light grid place-items-center shrink-0"><Award className="h-5 w-5 text-brand" /></div>
        <div className="min-w-0">
          <div className="font-heading font-bold text-brand">Oral Champion certificate</div>
          {cert ? (
            <div className="text-sm text-muted-foreground flex flex-wrap items-center gap-x-2">
              <FileText className="h-4 w-4 text-rag-green" />
              <a href={cert.url} target="_blank" rel="noreferrer" data-testid="champion-certificate-link" className="text-brand font-medium hover:underline inline-flex items-center gap-1 truncate max-w-[280px]">{cert.filename || "Certificate"}<ExternalLink className="h-3 w-3" /></a>
              <span>· uploaded {fmtDate(cert.uploaded_at)}</span>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Upload your champion's certificate (PDF or photo) so it's on file for inspectors and shows in your Evidence Pack overview.</p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <input ref={inputRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" data-testid="champion-certificate-input" onChange={(e) => upload(e.target.files?.[0])} />
        <Button data-testid="champion-certificate-upload-btn" onClick={() => inputRef.current?.click()} disabled={busy} className="rounded-full bg-brand hover:bg-brand-hover text-white">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}{cert ? "Replace" : "Upload certificate"}
        </Button>
        {cert && <Button data-testid="champion-certificate-remove-btn" variant="outline" onClick={remove} className="rounded-full"><Trash2 className="h-4 w-4" /></Button>}
      </div>
    </div>
  );
}
